"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { Apple, Download, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import Footer from "@/components/ui/footer";

/** Inline Microsoft 4-square logo */
const MicrosoftLogo = ({ size = 64 }: { size?: number }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23" width={size} height={size} aria-label="Microsoft logo">
        <rect x="1"  y="1"  width="10" height="10" fill="#f25022" />
        <rect x="12" y="1"  width="10" height="10" fill="#7fba00" />
        <rect x="1"  y="12" width="10" height="10" fill="#00a4ef" />
        <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
    </svg>
);

export default function DownloadPage() {
    const containerRef = useRef<HTMLDivElement>(null);
    const macBtnRef   = useRef<HTMLButtonElement>(null);
    const winBtnRef   = useRef<HTMLAnchorElement>(null);
    const titleRef    = useRef<HTMLHeadingElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.timeline()
                .from(titleRef.current, { y: 50, opacity: 0, duration: 1, ease: "power3.out" })
                .from([winBtnRef.current, macBtnRef.current], {
                    y: 30, opacity: 0, duration: 0.8, stagger: 0.2, ease: "back.out(1.7)"
                }, "-=0.5");
        }, containerRef);
        return () => ctx.revert();
    }, []);

    const steps = [
        { title: "Download",  desc: "Get the app directly from the Microsoft Store — no sideloading required." },
        { title: "Install",   desc: "Run the setup wizard to install Restaubee POS locally on your machine." },
        { title: "Scale",     desc: "Sign in and start scaling your restaurant business today." },
    ];

    return (
        <div ref={containerRef} className="min-h-screen bg-white font-sans flex flex-col relative overflow-x-hidden pt-32">

            <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 mb-20 lg:mb-32">
                <div className="text-center max-w-5xl mx-auto">

                    {/* Version badge */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-50/50 border border-teal-100 shadow-sm text-teal-900 text-sm font-medium mb-8"
                    >
                        <Sparkles className="w-4 h-4 text-teal-500" />
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-600 to-emerald-600 font-bold">Stable v2.4.0</span>
                        <span className="w-1 h-1 rounded-full bg-teal-200 mx-1" />
                        <span>Available for Windows &amp; Mac</span>
                    </motion.div>

                    <h1 ref={titleRef} className="text-6xl md:text-8xl font-bold text-gray-900 mb-8 tracking-tighter leading-tight">
                        Power up with <br />
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-600">
                            Restaubee POS
                        </span>
                    </h1>

                    <p className="text-xl md:text-2xl text-gray-500 max-w-2xl mx-auto leading-relaxed mb-16 font-medium">
                        Experience the world&apos;s most elegant restaurant management system,
                        natively optimized for your desktop.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">

                        {/* ──── Microsoft Store Card ──── */}
                        <a
                            ref={winBtnRef}
                            id="microsoft-store-btn"
                            href="https://get.microsoft.com/installer/download/9p9w500l6kwg?referrer=appbadge"
                            target="_self"
                            className="group relative w-full h-64 bg-white/40 backdrop-blur-xl border border-white/40 rounded-[2.5rem] hover:border-[#0078d4]/50 hover:shadow-2xl hover:shadow-[#0078d4]/10 transition-all duration-500 flex flex-col items-center justify-center gap-5 overflow-hidden p-8 no-underline"
                            style={{ textDecoration: "none" }}
                        >
                            {/* Blue ambient glow */}
                            <div className="absolute -top-10 -right-10 w-44 h-44 bg-[#0078d4]/5 rounded-full blur-3xl group-hover:bg-[#0078d4]/15 transition-colors duration-500 pointer-events-none" />

                            {/* Big Microsoft logo */}
                            <div className="w-20 h-20 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23" width="60" height="60" aria-label="Microsoft logo">
                                    <rect x="1"  y="1"  width="10" height="10" fill="#f25022" />
                                    <rect x="12" y="1"  width="10" height="10" fill="#7fba00" />
                                    <rect x="1"  y="12" width="10" height="10" fill="#00a4ef" />
                                    <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
                                </svg>
                            </div>

                            <div className="text-center">
                                <div className="font-extrabold text-gray-900 text-2xl mb-0.5">Windows</div>
                                <div className="text-sm text-gray-400 font-medium">Windows 10 &amp; 11 (64-bit)</div>
                            </div>

                            {/* Pill Button */}
                            <div className="bg-gray-900 text-white px-6 py-2.5 rounded-full text-sm font-bold flex items-center gap-2 group-hover:bg-[#0078d4] transition-colors shadow-md">
                                Microsoft Store
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23" width="16" height="16" aria-label="Microsoft logo" className="flex-shrink-0">
                                    <rect x="1"  y="1"  width="10" height="10" fill="#f25022" />
                                    <rect x="12" y="1"  width="10" height="10" fill="#7fba00" />
                                    <rect x="1"  y="12" width="10" height="10" fill="#00a4ef" />
                                    <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
                                </svg>
                            </div>
                        </a>

                        {/* ──── macOS Card ──── */}
                        <button
                            ref={macBtnRef}
                            id="mac-download-btn"
                            className="group relative w-full h-64 bg-white/40 backdrop-blur-xl border border-white/40 rounded-[2.5rem] hover:border-teal-400 hover:shadow-2xl hover:shadow-teal-500/10 transition-all duration-500 flex flex-col items-center justify-center gap-6 overflow-hidden p-8"
                        >
                            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-teal-500/5 rounded-full blur-3xl group-hover:bg-teal-500/10 transition-colors pointer-events-none" />

                            <div className="w-20 h-20 bg-gray-900 text-white rounded-[2rem] flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:bg-teal-700 transition-all duration-500">
                                <Apple className="w-10 h-10" />
                            </div>

                            <div className="text-center">
                                <div className="font-extrabold text-gray-900 text-2xl mb-1">macOS</div>
                                <div className="text-sm text-teal-600/60 font-medium">Intel &amp; Apple Silicon</div>
                            </div>

                            <div className="bg-gray-900 text-white px-6 py-2.5 rounded-full text-sm font-bold flex items-center gap-2 group-hover:bg-teal-600 transition-colors">
                                Download .dmg <Download className="w-4 h-4" />
                            </div>
                        </button>
                    </div>

                    {/* Trust line */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9 }}
                        className="mt-8 flex items-center justify-center gap-2 text-xs text-gray-400 font-medium"
                    >
                        <MicrosoftLogo size={14} />
                        <span>Verified &amp; certified on the Microsoft Store</span>
                        <span className="w-1 h-1 rounded-full bg-gray-300 mx-1" />
                        <span>No sideloading required</span>
                    </motion.div>
                </div>
            </div>

            {/* Installation Steps */}
            <div className="bg-white/40 backdrop-blur-md border-t border-teal-100/50 py-24">
                <div className="container mx-auto px-4 max-w-5xl">
                    <div className="text-center mb-16">
                        <h3 className="text-3xl font-bold text-gray-900 mb-4">Set up in minutes</h3>
                        <p className="text-gray-500 font-medium">Follow these three simple steps to get started with Restaubee.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                        {steps.map((step, i) => (
                            <div key={i} className="relative group">
                                <div className="mb-6 flex items-center gap-4">
                                    <div className="w-14 h-14 bg-teal-50 border border-teal-100 rounded-2xl flex items-center justify-center text-xl font-black text-teal-600 shadow-sm group-hover:bg-teal-600 group-hover:text-white transition-all duration-300">
                                        {i + 1}
                                    </div>
                                    <div className="h-px flex-1 bg-gradient-to-r from-teal-100 to-transparent md:block hidden" />
                                </div>
                                <h4 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h4>
                                <p className="text-gray-500 leading-relaxed text-sm font-medium">{step.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* System Requirements */}
            <div className="py-12 border-t border-gray-100">
                <div className="container mx-auto max-w-4xl text-center">
                    <div className="flex flex-wrap justify-center gap-8 text-[13px] font-bold text-gray-400 uppercase tracking-widest">
                        <span>4GB RAM Minimum</span>
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-200 mt-1.5 md:block hidden" />
                        <span>500MB Free Space</span>
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-200 mt-1.5 md:block hidden" />
                        <span>MacOS 12+ / Win 10+</span>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}
